import { isValidElement } from 'react';
import { Cargando, ErrorConReintento, SinCoincidencias } from './EstadoDePantalla';
import { motivoDeError } from './estados';
import { Plus } from 'lucide-react';
import BotonExportar from './BotonExportar';

// EL patrón único de lista, en todos los módulos. Mismo orden, mismos
// lugares, siempre:
//   [ Título + subtítulo ]                    [ Exportar ] [ + Acción ]
//   [ filtros… ]
//   tabla que respira, con columna de acciones a la derecha.
//
// columnas: [{ etiqueta, valor:(fila)=>texto, render?:(fila)=>JSX,
//              alineado?:'derecha', clase? }]
//   `valor` se usa para la exportación y como texto por defecto; `render`
//   es solo para la presentación (chips, etc.).
// acciones: (fila) => [{ etiqueta, onClick, peligro? }]
// exportar: { nombre, columnas?, obtenerTodo? }
//   `obtenerTodo` es lo que separa exportar la LISTA de exportar la
//   PÁGINA. Cuando la pantalla pagina contra el servidor tiene que darlo,
//   o el archivo saldrá con el primer centenar y nombre de catálogo
//   completo.
// paginador: el <Paginador/> ya armado. Va debajo de la tabla, siempre en
//   el mismo sitio en los catorce módulos.
export default function ListaEstandar({
  titulo,
  subtitulo,
  filtros,
  accionPrincipal,
  columnas,
  filas,
  cargando,
  vacio,
  acciones,
  claveFila = (f, i) => f.id ?? f.sku ?? i,
  exportar,
  nota,
  filtrosExtra,
  paginador,
  // LOS CUATRO ESTADOS (EXPERIENCIA E2, aditivo): quien pasa `error` y
  // `onReintentar` obtiene el error con su botón; quien pasa `hayFiltros`
  // obtiene el vacío de «ninguna coincide» en vez del de «no hay nada».
  error = null,
  onReintentar = null,
  hayFiltros = false,
  onLimpiarFiltros = null,
}) {
  // Solo se exporta lo que tiene `valor`. Una columna que únicamente se
  // pinta (una miniatura, un botón) no tiene texto que meter en un CSV, y
  // colarla reventaría la exportación al no encontrar su `valor`.
  const colsExport = (exportar?.columnas || columnas).filter((c) => c.valor);
  const alinear = (c) => (c.alineado === 'derecha' ? 'text-right' : 'text-left');

  // Cómo se llama un renglón, para nombrar sus botones de acción. La
  // primera columna con texto es siempre la que lo identifica.
  const primeraConTexto = columnas.find((c) => c.valor);
  const nombreFila = (fila) => {
    const v = primeraConTexto?.valor(fila);
    return v == null || v === '' ? null : String(v);
  };

  return (
    // `min-w-0` (EX-4): una tabla ancha dentro de una rejilla o de pestañas
    // no puede obligar a la página a crecer; se desplaza dentro de su caja.
    <div className="space-y-4 min-w-0">
      {/* Encabezado del módulo: se explica solo. */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{titulo}</h2>
          {subtitulo && <p className="text-sm text-gray-500 mt-0.5">{subtitulo}</p>}
        </div>
        {/* EN TELÉFONO LOS BOTONES SE ENVUELVEN (EXPERIENCIA E5): medido a
            390 px, tres botones en una fila sacaban la página a 417 px y el
            botón primario quedaba cortado. `flex-wrap` y el primario sin
            encogerse. */}
        <div className="flex items-center gap-2 flex-wrap">
          {filtrosExtra}
          <BotonExportar
            nombre={exportar?.nombre || 'export'}
            columnas={colsExport}
            filas={filas || []}
            obtenerTodo={exportar?.obtenerTodo}
            pie={exportar?.pie}
          />
          {/* LA ACCIÓN PRINCIPAL PUEDE VENIR YA PINTADA. Tres pantallas
              —Clientes, Reclamos de cliente y Accesos de auditor— pasaban
              aquí un <button> completo y esto leía `.etiqueta` y `.onClick`
              de un elemento de React: salía un botón azul con un «+» y
              nada más, que no hacía nada al pulsarlo. Lo midió la
              auditoría de botones del 19 de septiembre de 2026 (tres
              «SIN REACCIÓN VISIBLE» con rótulo vacío). Un elemento se
              pinta tal cual; el objeto {etiqueta, onClick} sigue igual. */}
          {accionPrincipal && (isValidElement(accionPrincipal) ? accionPrincipal : (
            <button
              type="button"
              onClick={accionPrincipal.onClick}
              className="inline-flex items-center gap-1.5 bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] text-white text-sm font-semibold px-3 py-2 rounded-lg whitespace-nowrap"
            >
              <Plus className="h-4 w-4" /> {accionPrincipal.etiqueta}
            </button>
          ))}
        </div>
      </div>

      {nota}

      {filtros && <div className="flex flex-wrap gap-2 items-center">{filtros}</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto max-w-full">
        <table className="w-full text-sm" aria-label={titulo}>
          <thead>
            {/* `scope` es lo que ata cada dato con su encabezado: sin él, un
                lector de pantalla lee «ACME-01 · 14 · DISPONIBLE» sin decir
                nunca qué es cada cosa. */}
            <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
              {columnas.map((c, i) => (
                <th key={i} scope="col" className={`px-4 py-3 ${alinear(c)}`}>{c.etiqueta}</th>
              ))}
              {acciones && <th scope="col" className="px-4 py-3 text-right">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {(filas || []).map((fila, i) => (
              <tr key={claveFila(fila, i)} className="border-b border-gray-100 hover:bg-gray-50 align-middle">
                {columnas.map((c, j) => (
                  <td key={j} className={`px-4 py-2.5 ${alinear(c)} ${c.clase || 'text-gray-800'}`}>
                    {c.render ? c.render(fila) : c.valor(fila)}
                  </td>
                ))}
                {acciones && (
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-3 justify-end">
                      {/* `|| []` NO ES DEFENSA GENÉRICA: es el defecto
                          del 25 de agosto de 2026, localizado.

                          `acciones` es una función que devuelve los
                          botones de UNA fila, y devolver `null` para una
                          fila que no tiene ninguno es el modo natural de
                          escribirla — lo hacen `Clientes` (para «Público
                          en general»), `Activos` y `Reabastecimiento`.
                          Aquí eso reventaba la pantalla entera con
                          «Cannot read properties of null (reading
                          'map')», y se llevaba por delante la lista de
                          clientes de una empresa real, dos veces en un
                          minuto.

                          El arreglo va AQUÍ y no en cada llamador: el
                          contrato razonable es «devuelve los botones, o
                          nada», y es este componente el que tiene que
                          saber leer «nada». Corregirlo en Clientes
                          habría dejado a Activos y Reabastecimiento
                          esperando su turno. */}
                      {(acciones(fila) || []).map((a, k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={a.onClick}
                          // «Editar» cincuenta veces seguidas no distingue
                          // nada. La primera columna es siempre lo que
                          // identifica el renglón (SKU, folio, serie), así
                          // que se le pega al nombre de la acción.
                          aria-label={nombreFila(fila) ? `${a.etiqueta}: ${nombreFila(fila)}` : undefined}
                          className={`text-sm font-semibold rounded focus:outline-none focus:ring-2
                                      focus:ring-[var(--color-marca)] focus:ring-offset-1 ${
                            a.peligro ? 'text-red-700 hover:text-red-800' : 'text-[var(--color-marca)] hover:text-[var(--color-marca-fuerte)]'}`}
                        >
                          {a.etiqueta}
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}

            {!cargando && !error && (!filas || filas.length === 0) && (
              <tr>
                <td colSpan={columnas.length + (acciones ? 1 : 0)} className="px-4 py-2">
                  {hayFiltros ? <SinCoincidencias onLimpiar={onLimpiarFiltros} /> : vacio}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* CARGANDO CON FILAS FANTASMA (H5): la tabla sin filas todavía
            enseña la forma de lo que viene, no un «Cargando…» suelto. Al
            RECARGAR con filas ya puestas, solo se anuncia: quitarlas y
            volver a ponerlas hace saltar la página bajo el dedo. */}
        {cargando && (!filas || filas.length === 0) && <div className="p-3"><Cargando filas={5} /></div>}
        {cargando && filas && filas.length > 0 && <div role="status" className="px-4 py-2 text-sm text-gray-600">Actualizando…</div>}
        {error && !cargando && (
          <div className="p-3">
            <ErrorConReintento que="No se pudo cargar la lista" porque={motivoDeError(error)} onReintentar={onReintentar} />
          </div>
        )}
      </div>

      {paginador}
    </div>
  );
}
